import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { useAppDispatch, useAppSelector } from "hooks";
import { useEffect, useMemo, useState } from "react";
import {
	onClosePopUp,
	onSetTutorialSlideNumber,
	selectDipState
} from "redux/slices";
import { Slide1, Slide2, Slide3, Slide4, Slide5 } from "./lib";

export const TutorialLoyaltyShop = () => {
	const dispatch = useAppDispatch();
	const [slideNumber, setSlideNumber] = useState(1);
	const { data: dipsData } = useAppSelector(selectDipState);
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { userTracker, postUserMetadata } = useWebStoreContext();
	const [oldPoints] = useState(userTracker?.loyaltyPoints || 0);
	// analytics only variables
	const texts: Record<number, string> = {
		1: "Earn Loyalty Points with every web purchase! Buy on the web and get LP automatically.",
		2: "Redeem LP for Wild Cards, Volcano Cards, Coins and Boosters.",
		3: "Kickstart your Loyalty Points journey with an AMAZING one-time offer! Your Rewards appear instantly on your balance.",
		4: "Here's your Loyalty Points balance, you can check it anytime!",
		5: "Replay the Loyalty Points tutorial anytime."
	};

	const isFirstTimeSeeen = useMemo(() => {
		return userTracker?.metadata?.hasSeenLPTutorial === "true";
	}, []);

	const dipFound = useMemo(() => {
		return dipsData?.dips?.find?.(
			(dip) => dip.dip.triggerType === "loyaltyFtueOfferQualified"
		);
	}, [dipsData]);

	const totalSlides = useMemo(() => {
		return dipFound ? 5 : 4;
	}, [dipFound]);

	const handlePopState = () => {
		dispatch(onClosePopUp());
		dispatch(onSetTutorialSlideNumber(0));
	};

	const handlePostData = async () => {
		await postUserMetadata({ key: "hasSeenLPTutorial", value: "true" });
	};

	const handleSkip = () => {
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "lp_ftue_completed",
			event_type: "lp_ftue",
			action: "lp_ftue_completed",
			source: "loyalty_shop",
			section_name: slideNumber === 1 ? "skipped" : "partially_watched",
			offer_id: dipFound ? dipFound?.dip?.id : "",
			type: isFirstTimeSeeen ? "replay_view" : "first_time_view",
			page_location: window.location.pathname
		});
		handlePostData();
		handlePopState();
	};

	const nextSlide = () => {
		// prevent dip tutorial slide if no dip is found
		if (!dipFound && slideNumber === 2) {
			setSlideNumber(slideNumber + 2);
			dispatch(onSetTutorialSlideNumber(slideNumber + 2));
			return;
		}
		setSlideNumber(slideNumber + 1);
		dispatch(onSetTutorialSlideNumber(slideNumber + 1));
	};

	useEffect(() => {
		if (slideNumber === 6) {
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "lp_ftue_completed",
				event_type: "lp_ftue",
				action: "lp_ftue_completed",
				source: "loyalty_shop",
				section_name: "completely_watched",
				offer_id: dipFound ? dipFound?.dip?.id : "",
				type: isFirstTimeSeeen ? "replay_view" : "first_time_view",
				page_location: window.location.pathname,
				value_int3: userTracker?.loyaltyPoints
			});
			handlePostData();
			handlePopState();
			return;
		}
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "lp_ftue_shown",
			event_type: "lp_ftue",
			action: "lp_ftue_shown",
			source: "loyalty_shop",
			section_name: slideNumber?.toString(),
			offer_id: dipFound ? dipFound?.dip?.id : "",
			type: isFirstTimeSeeen ? "replay_view" : "first_time_view",
			page_location: window.location.pathname,
			tag_text: texts[slideNumber],
			value_int3: userTracker?.loyaltyPoints
		});

		window.addEventListener("popstate", handlePopState);

		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, [slideNumber]);

	useEffect(() => {
		switch (location.pathname) {
			case "/loyalty-points":
			case "/loyalty-shop":
				// do nothing
				break;
			default:
				handlePopState();
				break;
		}
	}, [location.pathname]);

	// added extra scoll lock when tutorial is open
	useEffect(() => {
		document.body.style.overflow = "hidden";

		return () => {
			document.body.style.overflow = "";
		};
	}, []);

	return (
		<>
			{slideNumber === 1 && (
				<Slide1
					onNext={nextSlide}
					onSkip={handleSkip}
					slideNumber={totalSlides}
				/>
			)}
			{slideNumber === 2 && (
				<Slide2
					onNext={nextSlide}
					onSkip={handleSkip}
					slideNumber={totalSlides}
				/>
			)}
			{slideNumber === 3 && <Slide3 onNext={nextSlide} />}
			{slideNumber === 4 && (
				<Slide4
					points={oldPoints}
					onNext={nextSlide}
					onSkip={handleSkip}
					number={dipFound ? 4 : 3}
					slideNumber={totalSlides}
				/>
			)}
			{slideNumber === 5 && (
				<Slide5
					onNext={nextSlide}
					number={dipFound ? 5 : 4}
					slideNumber={totalSlides}
				/>
			)}
		</>
	);
};
